/* PasswordMeter.js */

class PasswordMeter {

    /**
     * @param {Waiting|null} waiting
     * @param {TemplateEngine} templateEngine
     */
    constructor(waiting, templateEngine, translator) {
        if (!translator) throw new Error('PasswordMeter: translator is required');
        this.waiting = waiting;
        this.templateEngine = templateEngine;
        this.translator = translator;

        /** @type {HTMLInputElement|null} */
        this.passwordNode = null;

        /** @type {HTMLElement|null} */
        this.wrapperNode = null;

        /** @type {HTMLElement|null} */
        this.meterNode = null;

        /** @type {HTMLButtonElement|null} */
        this.iconNode = null;

        this.templateEngine.getTemplateCollection()
            .add('pw-wrapper', `
                <div data-pw="wrapper">
                    <%inputGroup%>
                    <div data-pw="meter" class="mt-2">
                        <%meter%>
                    </div>
                </div>
            `)
            .add('pw-toggle', `
                <button
                    type="button"
                    data-pw="icon"
                    class="btn btn-outline-secondary"
                    aria-label="${this.translator.trans('password.show')}"
                    aria-pressed="false"
                >
                    <%icon%>
                </button>
            `)
            .add('pw-icon-visible', `
                <i
                    class="fa-regular fa-fw fa-eye"
                    aria-hidden="true"
                ></i>
            `)
            .add('pw-icon-hidden', `
                <i
                    class="fa-regular fa-fw fa-eye-slash"
                    aria-hidden="true"
                ></i>
            `)
            .add('pw-meter', `
                <div
                    class="d-flex gap-2"
                    role="meter"
                    aria-label="${this.translator.trans('password.security')}"
                    aria-valuenow="<%value%>"
                    aria-valuemin="0"
                    aria-valuemax="100"
                >
                    <%bars%>
                </div>
            `)
            .add('pw-meter-bar', `
                <div
                    class="flex-fill rounded-1"
                    style="
                        height: .375rem;
                        background-color: var(<%bgcolor%>);
                    "
                    aria-hidden="true"
                ></div>
            `)
        ;
    }

    /**
     * Initializes the password visibility control, strength meter,
     * and form submit spinner.
     *
     * @param {HTMLInputElement} password
     * @returns {void}
     */
    initialize(password) {
        if (!(password instanceof HTMLInputElement)) {
            throw new TypeError(
                'PasswordMeter.initialize expects an HTMLInputElement.'
            );
        }

        if (password.type !== 'password') {
            throw new TypeError(
                'PasswordMeter.initialize expects a password input.'
            );
        }

        const form = password.closest('form');
        const inputGroup = password.closest('.input-group');

        this.passwordNode = password;

        if (inputGroup instanceof HTMLElement) {
            this.initializeExistingInputGroup(inputGroup);
        } else {
            this.initializeStandaloneInput(password);
        }

        this.getPwIconNode().addEventListener('click', () => {
            this.togglePassword();
            this.updateIcon();
        });

        this.getPwPassword().addEventListener('input', () => {
            this.updateStrengthMeter();
        });

        if (form && !form.hasAttribute('data-spinner')) {
            form.setAttribute('data-spinner', 'submit');

            form.addEventListener(
                'submit',
                this.handleFormSubmit.bind(this)
            );
        }

        this.updateStrengthMeter();
    }

    /**
     * Enhances an already-rendered Bootstrap input group.
     *
     * @param {HTMLElement} inputGroup
     * @returns {void}
     */
    initializeExistingInputGroup(inputGroup) {
        const wrapper = document.createElement('div');

        wrapper.dataset.pw = 'wrapper';

        inputGroup.parentNode?.insertBefore(wrapper, inputGroup);
        wrapper.appendChild(inputGroup);

        const toggleButton = this.templateEngine.renderToElement(
            'pw-toggle',
            {
                icon: this.templateEngine.render('pw-icon-visible')
            }
        );

        if (!(toggleButton instanceof HTMLButtonElement)) {
            throw new Error(
                'Unable to render the password visibility button.'
            );
        }

        inputGroup.appendChild(toggleButton);

        const meter = document.createElement('div');

        meter.dataset.pw = 'meter';
        meter.className = 'mt-2';
        meter.innerHTML = this.renderMeter(0);

        wrapper.appendChild(meter);

        this.wrapperNode = wrapper;
        this.iconNode = toggleButton;
        this.meterNode = meter;
    }

    /**
     * Builds an input group when the password input is standalone.
     *
     * @param {HTMLInputElement} password
     * @returns {void}
     */
    initializeStandaloneInput(password) {
        const wrapper = document.createElement('div');

        wrapper.dataset.pw = 'wrapper';

        const inputGroup = document.createElement('div');

        inputGroup.className = 'input-group';

        const parentNode = password.parentNode;

        if (!parentNode) {
            throw new Error(
                'Password input has no parent node.'
            );
        }

        parentNode.insertBefore(wrapper, password);
        wrapper.appendChild(inputGroup);
        inputGroup.appendChild(password);

        const toggleButton = this.templateEngine.renderToElement(
            'pw-toggle',
            {
                icon: this.templateEngine.render('pw-icon-visible')
            }
        );

        if (!(toggleButton instanceof HTMLButtonElement)) {
            throw new Error(
                'Unable to render the password visibility button.'
            );
        }

        inputGroup.appendChild(toggleButton);

        const meter = document.createElement('div');

        meter.dataset.pw = 'meter';
        meter.className = 'mt-2';
        meter.innerHTML = this.renderMeter(0);

        wrapper.appendChild(meter);

        this.wrapperNode = wrapper;
        this.iconNode = toggleButton;
        this.meterNode = meter;
    }

    /**
     * @returns {HTMLElement}
     */
    getWrapperNode() {
        if (!(this.wrapperNode instanceof HTMLElement)) {
            throw new Error(
                'PasswordMeter has not been initialized.'
            );
        }

        return this.wrapperNode;
    }

    /**
     * @returns {HTMLButtonElement}
     */
    getPwIconNode() {
        if (!(this.iconNode instanceof HTMLButtonElement)) {
            throw new Error(
                'Password visibility button not found.'
            );
        }

        return this.iconNode;
    }

    /**
     * @returns {HTMLElement}
     */
    getPwMeterNode() {
        if (!(this.meterNode instanceof HTMLElement)) {
            throw new Error(
                'Password meter container not found.'
            );
        }

        return this.meterNode;
    }

    /**
     * @returns {HTMLInputElement}
     */
    getPwPassword() {
        if (!(this.passwordNode instanceof HTMLInputElement)) {
            throw new Error(
                'Password input not found.'
            );
        }

        return this.passwordNode;
    }

    /**
     * @returns {boolean}
     */
    isPasswordVisible() {
        return this.getPwPassword().type === 'text';
    }

    /**
     * Toggles the password input between visible and hidden.
     *
     * @returns {void}
     */
    togglePassword() {
        this.getPwPassword().type = this.isPasswordVisible()
            ? 'password'
            : 'text';
    }

    /**
     * Updates the icon and accessible state of the visibility button.
     *
     * @returns {void}
     */
    updateIcon() {
        const iconButton = this.getPwIconNode();
        const isVisible = this.isPasswordVisible();

        iconButton.innerHTML = this.templateEngine.render(
            isVisible
                ? 'pw-icon-hidden'
                : 'pw-icon-visible'
        );

        iconButton.setAttribute(
            'aria-pressed',
            String(isVisible)
        );

        const label = isVisible
            ? this.translator.trans('password.hide')
            : this.translator.trans('password.show');

        iconButton.setAttribute('aria-label', label);
    }

    /**
     * Updates the meter when the password value changes.
     *
     * @returns {void}
     */
    updateStrengthMeter() {
        const strength = this.calculatePasswordStrength(
            this.getPwPassword().value
        );

        this.getPwMeterNode().innerHTML =
            this.renderMeter(strength);
    }

    /**
     * Renders the four password-strength bars.
     *
     * @param {number} strength
     * @returns {string}
     */
    renderMeter(strength) {
        const activeBars = this.getActiveBarCount(strength);
        const activeColor = this.getBgColor(strength);

        let bars = '';

        for (let index = 0; index < 4; index += 1) {
            bars += this.templateEngine.render(
                'pw-meter-bar',
                {
                    bgcolor: index < activeBars
                        ? activeColor
                        : '--bs-secondary-bg'
                }
            );
        }

        return this.templateEngine.render('pw-meter', {
            value: strength,
            bars
        });
    }

    /**
     * Calculates password strength from the first typed character.
     *
     * Passwords shorter than eight characters are limited to 40,
     * so they cannot appear strong.
     *
     * @param {string} password
     * @returns {number}
     */
    calculatePasswordStrength(password) {
        if (password.length === 0) {
            return 0;
        }

        let score = 0;

        if (password.length >= 1) {
            score += 10;
        }

        if (password.length >= 4) {
            score += 10;
        }

        if (password.length >= 8) {
            score += 20;
        }

        if (password.length >= 12) {
            score += 20;
        }

        if (/[a-z]/.test(password)) {
            score += 10;
        }

        if (/[A-Z]/.test(password)) {
            score += 10;
        }

        if (/\d/.test(password)) {
            score += 10;
        }

        if (/[^A-Za-z0-9]/.test(password)) {
            score += 10;
        }

        if (/(.)\1{3,}/.test(password)) {
            score -= 20;
        }

        if (password.length < 8) {
            score = Math.min(score, 40);
        }

        return Math.max(
            0,
            Math.min(100, score)
        );
    }

    /**
     * Converts the strength score to the number of active bars.
     *
     * @param {number} strength
     * @returns {number}
     */
    getActiveBarCount(strength) {
        if (strength <= 0) {
            return 0;
        }

        if (strength <= 25) {
            return 1;
        }

        if (strength <= 50) {
            return 2;
        }

        if (strength <= 75) {
            return 3;
        }

        return 4;
    }

    /**
     * Returns the Bootstrap CSS variable for the current strength.
     *
     * @param {number} strength
     * @returns {string}
     */
    getBgColor(strength) {
        if (strength <= 25) {
            return '--bs-danger';
        }

        if (strength <= 50) {
            return '--bs-warning';
        }

        if (strength <= 75) {
            return '--bs-info';
        }

        return '--bs-success';
    }

    /**
     * Activates the waiting indicator on form submission.
     *
     * @returns {void}
     */
    handleFormSubmit() {
        if (this.waiting) {
            this.waiting.wait(true);
        }
    }
}

export default PasswordMeter;